import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";

// Material-UI and custom components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";
import Autocomplete from "@mui/material/Autocomplete";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import Backdrop from "@mui/material/Backdrop";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDSnackbar from "components/MDSnackbar";
import DataTable from "examples/Tables/DataTable";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useTranslation } from "react-i18next";
import * as XLSX from "xlsx";
import { getMdmAuthKeyByEmail } from "utils/getMdmAuthKeyByEmail";

// Context and utils
import { useUserContext } from "context/UserContext";
import getGraphQLEndpoint from "utils/getGraphQLEndpoint";
import { normalizeRoleLevel } from "utils/roleUtils";

const MAIN_WAREHOUSE_ID = 80;
const SHOW_CALIBRATION_ACTIONS = true;
const MOVEOUT_ALLOWED_EMAILS = new Set([
  "mntest@erp.astrophant.online",
  "bkadmin@erp.astrophant.online",
]);

const outlineButtonSx = {
  borderColor: "#1976d2",
  color: "#1976d2",
  fontWeight: 600,
  "&:hover": {
    borderColor: "#115293",
    backgroundColor: "rgba(25, 118, 210, 0.08)",
  },
  "&.Mui-disabled": {
    color: "rgba(25, 118, 210, 0.45)",
    borderColor: "rgba(25, 118, 210, 0.3)",
  },
};
const primaryButtonSx = {
  backgroundColor: "#1976d2",
  color: "#ffffff",
  fontWeight: 600,
  "&:hover": {
    backgroundColor: "#115293",
  },
  "&.Mui-disabled": {
    backgroundColor: "rgba(25, 118, 210, 0.35)",
    color: "rgba(255, 255, 255, 0.85)",
  },
};
const employeeActionButtonSx = {
  minWidth: 88,
  fontWeight: 700,
  borderWidth: "2px",
  color: "#1f5fd1",
  borderColor: "#2f6fe4",
  backgroundColor: "#ffffff",
  "&:hover": {
    borderColor: "#1a56c6",
    backgroundColor: "rgba(47, 111, 228, 0.08)",
  },
};
const employeeDisableButtonSx = {
  minWidth: 88,
  fontWeight: 700,
  borderWidth: "2px",
  color: "#1f5fd1",
  borderColor: "#2f6fe4",
  backgroundColor: "#ffffff",
  "&:hover": {
    borderColor: "#1a56c6",
    backgroundColor: "rgba(47, 111, 228, 0.08)",
  },
  "&.Mui-disabled": {
    color: "rgba(31, 95, 209, 0.38)",
    borderColor: "rgba(47, 111, 228, 0.22)",
    backgroundColor: "#ffffff",
  },
};
const selectSx = {
  "& .MuiOutlinedInput-root": {
    minHeight: 40,
  },
  "& .MuiSelect-select": {
    minHeight: 40,
    display: "flex",
    alignItems: "center",
    paddingTop: "10px",
    paddingBottom: "10px",
  },
};
const autocompleteSx = {
  "& .MuiOutlinedInput-root": {
    minHeight: 40,
  },
  "& .MuiAutocomplete-inputRoot": {
    paddingTop: "1px",
    paddingBottom: "1px",
  },
};

const TAIPEI_TIMEZONE = "Asia/Taipei";
const formatDateTimeToTaipei = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const raw = typeof value === "string" ? value.trim() : value;
  const numeric = typeof raw === "number" ? raw : Number(raw);
  const date =
    Number.isFinite(numeric) && String(raw).length <= 13
      ? new Date(numeric < 1e12 ? numeric * 1000 : numeric)
      : new Date(raw);
  if (Number.isNaN(date.getTime())) return String(value);
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: TAIPEI_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  })
    .formatToParts(date)
    .reduce((acc, part) => {
      acc[part.type] = part.value;
      return acc;
    }, {});
  return `${parts.year}-${parts.month}-${parts.day} ${parts.hour}:${parts.minute}:${parts.second}`;
};

const trimTrailingSlash = (value) => value.replace(/\/+$/, "");
const stripGraphQLSuffix = (value) => value.replace(/\/GraphQLHandler$/, "");
const buildBackendBase = (graphqlEndpoint) =>
  trimTrailingSlash(stripGraphQLSuffix(graphqlEndpoint || ""));
const resolveBackendBase = (graphqlEndpoint) => {
  const base = buildBackendBase(graphqlEndpoint);
  const fallback = typeof window !== "undefined" ? window.location.origin : "";
  if (!base || base === "undefined" || base === "null") return fallback;
  try {
    new URL(base);
    return base;
  } catch (error) {
    return fallback || base;
  }
};

const buildApiUrl = (base, path, params = {}) => {
  const url = new URL(path, base);
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    url.searchParams.set(key, value);
  });
  return url.toString();
};

const fetchJson = async (url, options = {}) => {
  const res = await fetch(url, options);
  if (!res.ok) {
    throw new Error(`Request failed (${res.status})`);
  }
  return res.json();
};

const fetchGraphQL = async (url, query, variables = {}) => {
  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify({ query, variables }),
  });
  const result = await response.json().catch(() => ({}));
  if (!response.ok || result?.errors?.length) {
    throw new Error(result?.errors?.[0]?.message || `Request failed (${response.status})`);
  }
  return result?.data || {};
};

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

const firstFilled = (...values) => {
  const found = values.find((value) => value !== null && value !== undefined && value !== "");
  return found === undefined ? "" : found;
};

const buildCalibrationStockOption = (stock) => {
  const sku = firstFilled(stock?.sku, stock?.barcode, stock?.ref, stock?.product_id);
  if (!sku) return null;
  const product = firstFilled(stock?.display_name, stock?.label, stock?.name, sku);
  const qty =
    stock?.qty !== null && stock?.qty !== undefined && stock?.qty !== "" ? Number(stock.qty) : null;

  return {
    label:
      qty === null || Number.isNaN(qty) ? `${product} (${sku})` : `${product} (${sku}) - ${qty}`,
    value: String(sku),
    product,
    qty,
  };
};

const buildCalibrationProductOption = (product) => {
  const sku = firstFilled(product?.barcode, product?.ref, product?.sku, product?.product_id);
  if (!sku) return null;
  const name = firstFilled(product?.label, product?.display_name, product?.name, sku);

  return {
    label: `${name} (${sku})`,
    value: String(sku),
    product: name,
    qty: null,
  };
};

const StockButtonCell = ({ row, onOpen, label }) => {
  const stocks = row.original.stocks || [];
  const title = row.original.label || row.original.ref || "";
  const warehouse = row.original || {};
  return (
    <Button
      size="small"
      variant="outlined"
      sx={outlineButtonSx}
      onClick={() => onOpen(stocks, title, warehouse)}
    >
      {label}
    </Button>
  );
};

StockButtonCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.object.isRequired,
  }).isRequired,
  onOpen: PropTypes.func.isRequired,
  label: PropTypes.string.isRequired,
};

const EmployeeActionCell = ({ row, onOpenEdit, onOpenDisable, editLabel, disableLabel }) => {
  const isInactive = row.original?.status !== "active";
  return (
    <MDBox display="flex" gap={1} flexWrap="wrap" alignItems="center">
      <Button
        size="small"
        variant="outlined"
        sx={employeeActionButtonSx}
        onClick={() => onOpenEdit(row.original)}
      >
        {editLabel}
      </Button>
      <Button
        size="small"
        variant="outlined"
        color="error"
        sx={employeeDisableButtonSx}
        disabled={isInactive}
        onClick={() => onOpenDisable(row.original)}
      >
        {disableLabel}
      </Button>
    </MDBox>
  );
};

EmployeeActionCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.object.isRequired,
  }).isRequired,
  onOpenEdit: PropTypes.func.isRequired,
  onOpenDisable: PropTypes.func.isRequired,
  editLabel: PropTypes.string.isRequired,
  disableLabel: PropTypes.string.isRequired,
};

const MoveoutActionCell = ({ row, canMoveOut, onOpen, label, disabledLabel }) => (
  <Button
    size="small"
    variant="outlined"
    sx={outlineButtonSx}
    disabled={!canMoveOut}
    onClick={() => onOpen(row?.original || {})}
  >
    {canMoveOut ? label : disabledLabel}
  </Button>
);

MoveoutActionCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.object.isRequired,
  }).isRequired,
  canMoveOut: PropTypes.bool.isRequired,
  onOpen: PropTypes.func.isRequired,
  label: PropTypes.string.isRequired,
  disabledLabel: PropTypes.string.isRequired,
};

const StockReconcileCell = ({ row, onOpen, label }) => {
  const canReconcile = Boolean(row.original?.warehouse_id);

  return (
    <Button
      size="small"
      variant="outlined"
      sx={outlineButtonSx}
      disabled={!canReconcile}
      onClick={(event) => {
        event.stopPropagation();
        onOpen(row.original);
      }}
    >
      {label}
    </Button>
  );
};

StockReconcileCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.object.isRequired,
  }).isRequired,
  onOpen: PropTypes.func.isRequired,
  label: PropTypes.string.isRequired,
};

function WarehousesBK() {
  const { userData, roleLevel, idToken, posPidArray } = useUserContext();
  const { t } = useTranslation(["routes", "common"]);
  const email = userData?.email ? userData.email.trim().toLowerCase() : "";
  const mdmAuthKey = useMemo(() => getMdmAuthKeyByEmail(email), [email]);
  const backendUrl = useMemo(getGraphQLEndpoint, []);
  const apiBase = useMemo(() => resolveBackendBase(backendUrl), [backendUrl]);
  const [metabaseUrl, setMetabaseUrl] = useState("");
  const [error, setError] = useState(null);

  const useAppsmith = false;
  const appsmithUrl = useMemo(() => {
    const url = new URL("https://app.appsmith.com/app/application/page1-68910adecfca0f3d24e667d2");
    url.searchParams.set("embed", "true");
    url.searchParams.set("hide_navigation_bar", "true");
    const userEmail = userData?.email ? userData.email.trim().toLowerCase() : "";
    if (userEmail) {
      url.searchParams.set("email", userEmail);
    }
    return url.toString();
  }, [userData?.email]);

  const numericRoleLevel = normalizeRoleLevel(roleLevel);
  const isBkRole = Number.isInteger(numericRoleLevel) && numericRoleLevel <= 4;
  const showMerchantFilter = numericRoleLevel === 3;
  const canMoveOut = useMemo(
    () => MOVEOUT_ALLOWED_EMAILS.has((email || "").toLowerCase()),
    [email]
  );

  const [activeTab, setActiveTab] = useState("main");
  const [loadingBk, setLoadingBk] = useState(false);
  const [bkError, setBkError] = useState("");
  const [missionSubmitting, setMissionSubmitting] = useState(false);
  const [missionSuccess, setMissionSuccess] = useState({ open: false, message: "" });
  const [missionError, setMissionError] = useState({ open: false, message: "" });

  const [warehouseData, setWarehouseData] = useState(null);
  const [missionSelectorData, setMissionSelectorData] = useState(null);
  const [missions, setMissions] = useState([]);
  const [stockLogs, setStockLogs] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [products, setProducts] = useState([]);
  const [venues, setVenues] = useState([]);
  const [merchantFilter, setMerchantFilter] = useState("BK");

  const [childMissionOpen, setChildMissionOpen] = useState(false);
  const [childMissionTab, setChildMissionTab] = useState("out");
  const [mainMissionOpen, setMainMissionOpen] = useState(false);
  const [employeeCreateOpen, setEmployeeCreateOpen] = useState(false);
  const [employeeEditOpen, setEmployeeEditOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [disableEmployeeTarget, setDisableEmployeeTarget] = useState(null);
  const [disableEmployeeSubmitting, setDisableEmployeeSubmitting] = useState(false);
  const [stockModal, setStockModal] = useState({ open: false, title: "", rows: [] });
  const [calibrationTarget, setCalibrationTarget] = useState(null);
  const [calibrationSubmitting, setCalibrationSubmitting] = useState(false);
  const [calibrationForm, setCalibrationForm] = useState({
    sku: "",
    targetQty: "",
    reason: "",
  });
  const [moveoutOpen, setMoveoutOpen] = useState(false);
  const [moveoutTarget, setMoveoutTarget] = useState(null);
  const [moveoutQty, setMoveoutQty] = useState("");
  const [moveoutThirdPartyId, setMoveoutThirdPartyId] = useState("");
  const [moveoutError, setMoveoutError] = useState("");
  const [moveoutSuccess, setMoveoutSuccess] = useState("");
  const [movingOut, setMovingOut] = useState(false);
  const [downloadDialogOpen, setDownloadDialogOpen] = useState(false);
  const [downloadFormat, setDownloadFormat] = useState("csv");

  const [childOutForm, setChildOutForm] = useState({
    fromWarehouseId: "",
    sku: "",
    qty: "",
    batchNo: "",
    employeeCode: "",
    note: "",
  });
  const [childInForm, setChildInForm] = useState({
    toWarehouseId: "",
    sku: "",
    qty: "",
    batchNo: "",
    employeeCode: "",
    note: "",
    ref: "",
    venueId: "",
  });
  const [mainInForm, setMainInForm] = useState({
    sku: "",
    qty: "",
    batchNo: "",
    employeeCode: "",
    note: "",
  });
  const [employeeCreateForm, setEmployeeCreateForm] = useState({
    code: "",
    firstName: "",
    lastName: "",
  });
  const [employeeEditForm, setEmployeeEditForm] = useState({
    code: "",
    firstName: "",
    lastName: "",
    status: "",
    originStatus: "",
  });

  // =================================================================
  // SECTION 1: Metabase embed for SP (keep existing logic)
  // =================================================================
  useEffect(() => {
    const fetchMetabaseUrl = async () => {
      if (numericRoleLevel) return;
      if (!idToken) {
        setError(t("warehousesPage.messages.authRequired"));
        return;
      }

      try {
        const pids = Array.isArray(posPidArray)
          ? posPidArray
              .map((item) => (item?.pos_pid ? String(item.pos_pid).trim() : ""))
              .filter(Boolean)
          : [];
        const response = await fetch(backendUrl, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${idToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            query: `
              query GetMetabaseURL($pids: [String!]!) {
                GetMetabaseEmbedURLwithPIDforTransaction(pids: $pids)
              }
            `,
            variables: { pids },
          }),
        });

        if (!response.ok) throw new Error("Failed to fetch Metabase URL");
        const data = await response.json();
        setMetabaseUrl(data.data.GetMetabaseEmbedURLwithPIDforTransaction);
      } catch (err) {
        console.error(err);
        setError(t("warehousesPage.messages.metabaseLoadFailed"));
      }
    };

    fetchMetabaseUrl();
  }, [numericRoleLevel, idToken, posPidArray, backendUrl, t]);

  // =================================================================
  // SECTION 2: BK data fetching
  // =================================================================
  const fetchWarehouseData = useCallback(async () => {
    if (!email) return null;
    try {
      const data = await fetchGraphQL(
        backendUrl,
        `
          query GetWarehouseQueryV2($email: String!) {
            GetWarehouseQueryV2(email: $email)
          }
        `,
        { email }
      );
      const payload = data?.GetWarehouseQueryV2?.data || {};
      setWarehouseData(payload);
      return payload;
    } catch (err) {
      console.error(err);
      setBkError((prev) => prev || t("warehousesPage.messages.loadWarehouseFailed"));
      return null;
    }
  }, [backendUrl, email, t]);

  const fetchMissionSelectorData = useCallback(async () => {
    if (!email) return null;
    try {
      const data = await fetchGraphQL(
        backendUrl,
        `
          query GetWarehouseQueryV2($email: String!) {
            GetWarehouseQueryV2(email: $email)
          }
        `,
        { email }
      );
      const payload = data?.GetWarehouseQueryV2?.data || {};
      setMissionSelectorData(payload);
      return payload;
    } catch (err) {
      console.error(err);
      setBkError((prev) => prev || t("warehousesPage.messages.loadWarehouseFailed"));
      return null;
    }
  }, [backendUrl, email, t]);

  const fetchMissions = useCallback(async () => {
    if (!email) return [];
    try {
      const data = await fetchGraphQL(
        backendUrl,
        `
          query GetStockMissionList($email: String!) {
            GetStockMissionList(email: $email)
          }
        `,
        { email }
      );
      const payload = Array.isArray(data?.GetStockMissionList?.data)
        ? data.GetStockMissionList.data
        : [];
      setMissions(payload);
      return payload;
    } catch (err) {
      console.error(err);
      setBkError((prev) => prev || t("warehousesPage.messages.loadMissionsFailed"));
      return [];
    }
  }, [backendUrl, email, t]);

  const fetchStockLogs = useCallback(async () => {
    if (!email) return [];
    try {
      const data = await fetchGraphQL(
        backendUrl,
        `
          query GetMainWarehouseStock($email: String!, $orderBy: String) {
            GetMainWarehouseStock(email: $email, orderBy: $orderBy)
          }
        `,
        { email, orderBy: "DESC" }
      );
      const payload = Array.isArray(data?.GetMainWarehouseStock?.data)
        ? data.GetMainWarehouseStock.data
        : [];
      setStockLogs(payload);
      return payload;
    } catch (err) {
      console.error(err);
      setBkError((prev) => prev || t("warehousesPage.messages.loadLogsFailed"));
      return [];
    }
  }, [backendUrl, email, t]);

  const fetchEmployees = useCallback(async () => {
    if (!email) return [];
    try {
      const data = await fetchGraphQL(
        backendUrl,
        `
          query GetEmployeeList($email: String!) {
            GetEmployeeList(email: $email)
          }
        `,
        { email }
      );
      const json = data?.GetEmployeeList || {};
      const payload = Array.isArray(json.data) ? json.data : [];
      setEmployees(payload);
      return payload;
    } catch (err) {
      console.error(err);
      setBkError((prev) => prev || t("warehousesPage.messages.loadEmployeesFailed"));
      return [];
    }
  }, [backendUrl, email, t]);

  const fetchProducts = useCallback(async () => {
    if (!email) return [];
    try {
      const data = await fetchGraphQL(
        backendUrl,
        `
          query GetProductListV2($email: String!, $status: String) {
            GetProductListV2(email: $email, status: $status)
          }
        `,
        { email, status: "1" }
      );
      const payload = Array.isArray(data?.GetProductListV2?.data) ? data.GetProductListV2.data : [];
      setProducts(payload);
      return payload;
    } catch (err) {
      console.error(err);
      setBkError((prev) => prev || t("warehousesPage.messages.loadProductsFailed"));
      return [];
    }
  }, [backendUrl, email, t]);

  const fetchVenues = useCallback(async () => {
    if (!email) return [];
    try {
      const data = await fetchGraphQL(
        backendUrl,
        `
          query GetVenueListV2($email: String!) {
            GetVenueListV2(email: $email)
          }
        `,
        { email }
      );
      const payload = Array.isArray(data?.GetVenueListV2?.data) ? data.GetVenueListV2.data : [];
      setVenues(payload);
      return payload;
    } catch (err) {
      console.error(err);
      setBkError((prev) => prev || t("warehousesPage.messages.loadVenuesFailed"));
      return [];
    }
  }, [backendUrl, email, t]);

  const refreshAll = useCallback(async () => {
    setLoadingBk(true);
    setBkError("");
    await Promise.all([
      fetchWarehouseData(),
      fetchMissionSelectorData(),
      fetchMissions(),
      fetchStockLogs(),
      fetchEmployees(),
      fetchProducts(),
      fetchVenues(),
    ]);
    setLoadingBk(false);
  }, [
    fetchEmployees,
    fetchMissions,
    fetchMissionSelectorData,
    fetchProducts,
    fetchStockLogs,
    fetchVenues,
    fetchWarehouseData,
  ]);

  useEffect(() => {
    if (!isBkRole || useAppsmith) return;
    if (!email) return;
    refreshAll();
  }, [email, isBkRole, refreshAll, useAppsmith]);

  useEffect(() => {
    if (!isBkRole || useAppsmith) return;
    if (!email) return;
    fetchEmployees();
  }, [email, fetchEmployees, isBkRole, merchantFilter, useAppsmith]);

  // =================================================================
  // SECTION 3: Derived data
  // =================================================================
  const mainWarehouseRows = useMemo(() => {
    if (!warehouseData) return [];
    return [
      {
        label: warehouseData.label || "",
        description: warehouseData.description || "",
        statut: warehouseData.statut ?? "",
        warehouse_id: warehouseData.warehouse_id ?? "",
        machine_tp_id: warehouseData.array_options?.options_machine_tp_id ?? "",
        role: warehouseData.array_options?.options_role ?? "",
        stocks: Array.isArray(warehouseData.stocks) ? warehouseData.stocks : [],
      },
    ];
  }, [warehouseData]);

  const childWarehouseRows = useMemo(() => {
    const children = Array.isArray(warehouseData?.children) ? warehouseData.children : [];
    return children.map((child) => ({
      ...child,
      machine_tp_id: child?.array_options?.options_machine_tp_id ?? child?.machine_tp_id ?? "",
      stocks: Array.isArray(child.stocks) ? child.stocks : [],
    }));
  }, [warehouseData]);

  const missionRows = useMemo(() => {
    const list = Array.isArray(missions) ? [...missions] : [];
    return list.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }, [missions]);

  const stockLogRows = useMemo(() => (Array.isArray(stockLogs) ? stockLogs : []), [stockLogs]);

  const employeeRows = useMemo(() => {
    const list = Array.isArray(employees) ? [...employees] : [];
    return list.sort((a, b) => {
      const dateA = new Date(a.created_at || a.CreatedAt || 0);
      const dateB = new Date(b.created_at || b.CreatedAt || 0);
      return dateB - dateA;
    });
  }, [employees]);

  const stockModalRows = useMemo(
    () => (Array.isArray(stockModal.rows) ? stockModal.rows : []),
    [stockModal.rows]
  );

  const calibrationStockOptions = useMemo(() => {
    const stocks = Array.isArray(calibrationTarget?.stocks) ? calibrationTarget.stocks : [];
    const stockOptions = stocks.map(buildCalibrationStockOption).filter(Boolean);
    const productOptions = (Array.isArray(products) ? products : [])
      .filter(
        (product) =>
          product?.status === undefined ||
          product?.status === "" ||
          product.status === "1" ||
          product.status === 1
      )
      .map(buildCalibrationProductOption)
      .filter(Boolean);

    return [...stockOptions, ...productOptions].reduce((acc, option) => {
      if (!acc.some((item) => item.value === option.value)) {
        acc.push(option);
      }
      return acc;
    }, []);
  }, [calibrationTarget, products]);

  const selectedCalibrationStock = useMemo(
    () => calibrationStockOptions.find((option) => option.value === calibrationForm.sku) || null,
    [calibrationForm.sku, calibrationStockOptions]
  );

  const calibrationWarehouseId = Number(calibrationTarget?.warehouse_id || 0);
  const calibrationWarehouseName = firstFilled(
    calibrationTarget?.label,
    calibrationTarget?.name,
    calibrationTarget?.ref,
    calibrationTarget?.warehouse_id,
    "-"
  );

  const childWarehouseOptions = useMemo(() => {
    const children = Array.isArray(missionSelectorData?.children)
      ? missionSelectorData.children
      : [];
    return children.map((child) => ({
      label: child.label || String(child.warehouse_id),
      value: String(child.warehouse_id),
    }));
  }, [missionSelectorData]);

  const mainStockOptions = useMemo(() => {
    const stocks = Array.isArray(missionSelectorData?.stocks) ? missionSelectorData.stocks : [];
    return stocks.map((item) => ({
      label: item.label || item.sku || "",
      value: item.sku || "",
      productId: item.product_id ?? "",
      price:
        item.price !== null && item.price !== undefined && item.price !== ""
          ? Number(item.price)
          : null,
    }));
  }, [missionSelectorData]);

  const selectedChildInStock = useMemo(
    () => mainStockOptions.find((option) => option.value === childInForm.sku) || null,
    [childInForm.sku, mainStockOptions]
  );

  const childOutSkuOptions = useMemo(() => {
    const missionChildren = Array.isArray(missionSelectorData?.children)
      ? missionSelectorData.children
      : [];
    const selected = missionChildren.find(
      (child) => String(child.warehouse_id) === childOutForm.fromWarehouseId
    );
    const stocks = Array.isArray(selected?.stocks) ? selected.stocks : [];
    return stocks.map((item) => ({
      label: item.label || item.sku || "",
      value: item.sku || "",
    }));
  }, [childOutForm.fromWarehouseId, missionSelectorData]);

  const productSkuOptions = useMemo(() => {
    const list = Array.isArray(products) ? products : [];
    return list
      .filter((item) => item.status === "1" || item.status === 1)
      .map((item) => ({
        label: item.label || item.ref || item.barcode || "",
        value: item.barcode || item.ref || "",
      }));
  }, [products]);

  const employeeOptions = useMemo(() => {
    const list = Array.isArray(employees) ? employees : [];
    return list
      .filter((item) => item?.status === "active")
      .map((item) => ({
        label: `${item.first_name || ""} ${item.last_name || ""}`.trim() || item.code,
        value: item.code || "",
      }));
  }, [employees]);

  const venueOptions = useMemo(() => {
    const list = Array.isArray(venues) ? venues : [];
    return list
      .map((item) => ({
        label: item.venue?.name || "",
        value: item.venue?.id || "",
      }))
      .filter((item) => item.value);
  }, [venues]);

  // =================================================================
  // SECTION 4: Table definitions
  // =================================================================
  const handleOpenStockModal = useCallback((stocks, title, warehouse = {}) => {
    const warehouseId = firstFilled(warehouse?.warehouse_id, warehouse?.warehouseId);
    const machineTpId = firstFilled(
      warehouse?.array_options?.options_machine_tp_id,
      warehouse?.machine_tp_id,
      warehouse?.machineTpId
    );
    setStockModal({
      open: true,
      title: title || "",
      rows: Array.isArray(stocks)
        ? stocks.map((stock) => ({
            ...stock,
            warehouse_id: firstFilled(stock?.warehouse_id, stock?.warehouseId, warehouseId),
            machine_tp_id: firstFilled(stock?.machine_tp_id, stock?.machineTpId, machineTpId),
          }))
        : [],
    });
  }, []);

  const handleCloseStockModal = useCallback(() => {
    setStockModal({ open: false, title: "", rows: [] });
  }, []);

  const handleOpenCalibration = useCallback((warehouse) => {
    const stocks = Array.isArray(warehouse?.stocks) ? warehouse.stocks : [];
    const firstOption = stocks.map(buildCalibrationStockOption).find(Boolean);

    // console.log(warehouse);
    // console.log({ firstOption });
    // console.log({ stocks });

    setCalibrationTarget({
      ...warehouse,
      stocks,
    });
    setCalibrationForm({
      sku: firstOption?.value || "",
      targetQty:
        firstOption?.qty !== null &&
        firstOption?.qty !== undefined &&
        !Number.isNaN(firstOption.qty)
          ? String(firstOption.qty)
          : "",
      reason: "",
    });
  }, []);

  const handleCloseCalibration = useCallback(() => {
    setCalibrationTarget(null);
    setCalibrationForm({ sku: "", targetQty: "", reason: "" });
  }, []);

  const applyCalibrationResult = useCallback(
    (target, sku, nextQty) => {
      const targetWarehouseId = Number(target?.warehouse_id || 0);
      const normalizedSku = String(sku || "");
      if (!targetWarehouseId || !normalizedSku) return;

      const updateStocks = (stocks = []) => {
        let matched = false;
        const nextStocks = (Array.isArray(stocks) ? stocks : []).map((stock) => {
          const stockSku = String(
            firstFilled(stock?.sku, stock?.barcode, stock?.ref, stock?.product_id)
          );
          if (stockSku !== normalizedSku) return stock;
          matched = true;
          return {
            ...stock,
            qty: nextQty,
          };
        });

        if (!matched) {
          nextStocks.push({
            sku: normalizedSku,
            label: selectedCalibrationStock?.product || normalizedSku,
            qty: nextQty,
          });
        }

        return nextStocks;
      };

      const updateWarehouse = (warehouse) => {
        if (!warehouse) return warehouse;
        const isTargetWarehouse = Number(warehouse.warehouse_id || 0) === targetWarehouseId;
        return {
          ...warehouse,
          stocks: isTargetWarehouse ? updateStocks(warehouse.stocks) : warehouse.stocks,
          children: Array.isArray(warehouse.children)
            ? warehouse.children.map(updateWarehouse)
            : warehouse.children,
        };
      };

      setWarehouseData((prevData) => updateWarehouse(prevData));
      setStockModal((prevModal) => {
        if (!prevModal.open) return prevModal;
        return {
          ...prevModal,
          rows: updateStocks(prevModal.rows),
        };
      });
    },
    [selectedCalibrationStock]
  );

  const handleSubmitCalibration = useCallback(async () => {
    const rawTargetQty = String(calibrationForm.targetQty).trim();
    const targetQtyValue = Number(calibrationForm.targetQty);

    if (
      !calibrationWarehouseId ||
      !calibrationForm.sku ||
      rawTargetQty === "" ||
      !Number.isInteger(targetQtyValue)
    ) {
      alert(t("warehousesPage.messages.requiredFields"));
      return;
    }

    if (targetQtyValue < 0) {
      alert("目標庫存數量不可小於 0");
      return;
    }

    setCalibrationSubmitting(true);
    try {
      const response = await fetch(buildApiUrl(apiBase, "/api/v1/stock/reconcile"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "Mdm-Auth": mdmAuthKey,
        },
        body: JSON.stringify({
          warehouse_id: calibrationWarehouseId,
          sku: calibrationForm.sku,
          target_qty: targetQtyValue,
          reason: calibrationForm.reason,
        }),
      });

      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(result?.message || result?.error || `校正失敗 (${response.status})`);
      }

      setMissionSuccess({ open: true, message: "機台庫存校正成功" });
      const reconciledTarget = calibrationTarget;
      const reconciledSku = calibrationForm.sku;
      applyCalibrationResult(reconciledTarget, reconciledSku, targetQtyValue);
      handleCloseCalibration();
      await Promise.all([fetchWarehouseData(), fetchStockLogs()]);
      applyCalibrationResult(reconciledTarget, reconciledSku, targetQtyValue);
    } catch (error) {
      console.error(error);
      setMissionError({ open: true, message: error.message || "機台庫存校正失敗" });
    } finally {
      setCalibrationSubmitting(false);
    }
  }, [
    apiBase,
    applyCalibrationResult,
    calibrationForm,
    calibrationTarget,
    calibrationWarehouseId,
    fetchStockLogs,
    fetchWarehouseData,
    handleCloseCalibration,
    mdmAuthKey,
    t,
  ]);

  const handleOpenMoveout = useCallback((target) => {
    setMoveoutTarget(target);
    setMoveoutQty(target?.qty ?? "");
    setMoveoutThirdPartyId(
      firstFilled(
        target?.machine_tp_id,
        target?.machineTpId,
        target?.array_options?.options_machine_tp_id
      )
    );
    setMoveoutError("");
    setMoveoutSuccess("");
    setMoveoutOpen(true);
  }, []);

  const handleCloseMoveout = useCallback(() => {
    setMoveoutOpen(false);
    setMoveoutTarget(null);
    setMoveoutQty("");
    setMoveoutThirdPartyId("");
    setMoveoutError("");
  }, []);

  const handleConfirmMoveout = useCallback(async () => {
    if (!canMoveOut) {
      setMoveoutError(t("warehousesPage.messages.moveoutPermission"));
      return;
    }
    if (!email) {
      setMoveoutError(t("warehousesPage.messages.moveoutNeedEmail"));
      return;
    }
    const sku = moveoutTarget?.sku || moveoutTarget?.product_id || "";
    if (!sku) {
      setMoveoutError(t("warehousesPage.messages.moveoutNeedSku"));
      return;
    }
    const thirdPartyId = Number(moveoutThirdPartyId);
    if (!Number.isFinite(thirdPartyId) || thirdPartyId <= 0) {
      setMoveoutError(t("warehousesPage.messages.moveoutNeedThirdParty"));
      return;
    }
    const quantity = Number(moveoutQty);
    if (!Number.isFinite(quantity) || quantity <= 0) {
      setMoveoutError(t("warehousesPage.messages.moveoutNeedQty"));
      return;
    }

    setMovingOut(true);
    setMoveoutError("");
    setMoveoutSuccess("");
    try {
      const body = {
        email,
        sku,
        third_party_id: thirdPartyId,
        quantity,
      };
      await fetchJson(buildApiUrl(apiBase, "/api/v1/warehouse/moveout"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      setMoveoutSuccess(t("warehousesPage.messages.moveoutSuccess"));
      await fetchStockLogs();
      await fetchWarehouseData();
      setMoveoutOpen(false);
    } catch (error) {
      console.error(error);
      setMoveoutError(t("warehousesPage.messages.moveoutFailed"));
    } finally {
      setMovingOut(false);
    }
  }, [
    apiBase,
    canMoveOut,
    email,
    fetchStockLogs,
    fetchWarehouseData,
    moveoutQty,
    moveoutTarget,
    moveoutThirdPartyId,
    t,
  ]);

  const handleOpenEmployeeEdit = useCallback((employee) => {
    setEditingEmployee(employee);
    setEmployeeEditForm({
      code: employee.code || "",
      firstName: employee.first_name || "",
      lastName: employee.last_name || "",
      status: employee.status === "active" ? "active" : "inactive",
      originStatus: employee.status === "active" ? "active" : "inactive",
    });
    setEmployeeEditOpen(true);
  }, []);

  const handleOpenEmployeeDisable = useCallback((employee) => {
    if (!employee?.code || employee.status !== "active") return;
    setDisableEmployeeTarget(employee);
  }, []);

  const handleCloseEmployeeDisable = useCallback(() => {
    if (disableEmployeeSubmitting) return;
    setDisableEmployeeTarget(null);
  }, [disableEmployeeSubmitting]);

  const mainWarehouseColumns = useMemo(
    () => [
      { Header: t("warehousesPage.columns.mainWarehouse.label"), accessor: "label" },
      { Header: t("warehousesPage.columns.mainWarehouse.description"), accessor: "description" },
      { Header: t("warehousesPage.columns.mainWarehouse.status"), accessor: "statut" },
      { Header: t("warehousesPage.columns.mainWarehouse.warehouseId"), accessor: "warehouse_id" },
      { Header: t("warehousesPage.columns.mainWarehouse.machineTpId"), accessor: "machine_tp_id" },
      { Header: t("warehousesPage.columns.mainWarehouse.role"), accessor: "role" },
      {
        Header: t("warehousesPage.columns.mainWarehouse.stockAction"),
        accessor: "stocks",
        Cell: (props) => (
          <StockButtonCell
            {...props}
            onOpen={handleOpenStockModal}
            label={t("warehousesPage.actions.stock")}
          />
        ),
      },
      ...(SHOW_CALIBRATION_ACTIONS
        ? [
            {
              Header: t("warehousesPage.columns.mainWarehouse.stockReconcile"),
              accessor: "stock_reconcile",
              Cell: (props) => (
                <StockReconcileCell {...props} onOpen={handleOpenCalibration} label="校正" />
              ),
            },
          ]
        : []),
    ],
    [handleOpenCalibration, handleOpenStockModal, t]
  );

  const childWarehouseColumns = useMemo(
    () => [
      { Header: t("warehousesPage.columns.childWarehouse.ref"), accessor: "ref" },
      { Header: t("warehousesPage.columns.childWarehouse.label"), accessor: "label" },
      { Header: t("warehousesPage.columns.childWarehouse.description"), accessor: "description" },
      { Header: t("warehousesPage.columns.childWarehouse.status"), accessor: "statut" },
      { Header: t("warehousesPage.columns.childWarehouse.warehouseId"), accessor: "warehouse_id" },
      {
        Header: t("warehousesPage.columns.childWarehouse.stocks"),
        accessor: "stocks",
        Cell: ({ value }) => (Array.isArray(value) ? value.length : 0),
      },
      {
        Header: t("warehousesPage.columns.childWarehouse.stockAction"),
        accessor: "stocks_action",
        Cell: (props) => (
          <StockButtonCell
            {...props}
            onOpen={handleOpenStockModal}
            label={t("warehousesPage.actions.stock")}
          />
        ),
      },
      ...(SHOW_CALIBRATION_ACTIONS
        ? [
            {
              Header: t("warehousesPage.columns.childWarehouse.stockReconcile"),
              accessor: "stock_reconcile",
              Cell: (props) => (
                <StockReconcileCell {...props} onOpen={handleOpenCalibration} label="校正" />
              ),
            },
          ]
        : []),
    ],
    [handleOpenCalibration, handleOpenStockModal, t]
  );

  const missionColumns = useMemo(
    () => [
      { Header: t("warehousesPage.columns.mission.createdAt"), accessor: "created_at" },
      { Header: t("warehousesPage.columns.mission.productName"), accessor: "product_name" },
      { Header: t("warehousesPage.columns.mission.sku"), accessor: "sku" },
      { Header: t("warehousesPage.columns.mission.productId"), accessor: "product_id" },

      {
        Header: t("warehousesPage.columns.mission.fromWarehouseId"),
        accessor: "from_warehouse_id",
      },
      { Header: t("warehousesPage.columns.mission.toWarehouseId"), accessor: "to_warehouse_id" },
      {
        Header: t("warehousesPage.columns.mission.fromExpectedQty"),
        accessor: "from_expected_qty",
      },
      { Header: t("warehousesPage.columns.mission.toExpectedQty"), accessor: "to_expected_qty" },
      { Header: t("warehousesPage.columns.mission.fromActualQty"), accessor: "from_actual_qty" },
      { Header: t("warehousesPage.columns.mission.toActualQty"), accessor: "to_actual_qty" },
      { Header: t("warehousesPage.columns.mission.executedBy"), accessor: "executed_by" },
      { Header: t("warehousesPage.columns.mission.missionType"), accessor: "mission_type" },
      { Header: t("warehousesPage.columns.mission.note"), accessor: "note" },
      { Header: t("warehousesPage.columns.mission.fromStatus"), accessor: "from_status" },
      { Header: t("warehousesPage.columns.mission.toStatus"), accessor: "to_status" },
      {
        Header: t("warehousesPage.columns.mission.fromCompletedAt"),
        accessor: "from_completed_at",
      },
      { Header: t("warehousesPage.columns.mission.toCompletedAt"), accessor: "to_completed_at" },
      { Header: t("warehousesPage.columns.mission.id"), accessor: "id" },
      {
        Header: t("warehousesPage.columns.mission.fromTransactionId"),
        accessor: "from_transaction_id",
      },
      {
        Header: t("warehousesPage.columns.mission.toTransactionId"),
        accessor: "to_transaction_id",
      },
      {
        Header: t("warehousesPage.columns.mission.fromStockMovementId"),
        accessor: "from_stock_movement_id",
      },
      {
        Header: t("warehousesPage.columns.mission.toStockMovementId"),
        accessor: "to_stock_movement_id",
      },
      { Header: t("warehousesPage.columns.mission.fromExecutedAt"), accessor: "from_executed_at" },
      { Header: t("warehousesPage.columns.mission.toExecutedAt"), accessor: "to_executed_at" },

      { Header: t("warehousesPage.columns.mission.createdBy"), accessor: "created_by" },
    ],
    [t]
  );

  const stockLogColumns = useMemo(
    () => [
      { Header: t("warehousesPage.columns.stockLog.warehouseId"), accessor: "warehouse_id" },
      { Header: t("warehousesPage.columns.stockLog.type"), accessor: "type" },
      { Header: t("warehousesPage.columns.stockLog.label"), accessor: "label" },

      { Header: t("warehousesPage.columns.stockLog.qty"), accessor: "qty" },
      {
        Header: t("warehousesPage.columns.stockLog.date"),
        accessor: "date",
        Cell: ({ value }) => formatDateTimeToTaipei(value),
      },
      { Header: t("warehousesPage.columns.stockLog.id"), accessor: "id" },
      { Header: t("warehousesPage.columns.stockLog.productId"), accessor: "product_id" },
    ],
    [t]
  );

  const employeeColumns = useMemo(
    () => [
      {
        Header: t("warehousesPage.columns.employee.actions"),
        accessor: "actions",
        Cell: (props) => (
          <EmployeeActionCell
            {...props}
            onOpenEdit={handleOpenEmployeeEdit}
            onOpenDisable={handleOpenEmployeeDisable}
            editLabel={t("warehousesPage.actions.edit")}
            disableLabel="停用"
          />
        ),
      },
      {
        Header: "狀態",
        accessor: "status",
        Cell: ({ value }) => (value === "active" ? "啟用" : "停用"),
      },
      { Header: t("warehousesPage.columns.employee.id"), accessor: "id" },
      { Header: t("warehousesPage.columns.employee.firstName"), accessor: "first_name" },
      { Header: t("warehousesPage.columns.employee.lastName"), accessor: "last_name" },
      { Header: t("warehousesPage.columns.employee.code"), accessor: "code" },
      { Header: t("warehousesPage.columns.employee.merchantId"), accessor: "merchant_id" },
      { Header: t("warehousesPage.columns.employee.token"), accessor: "token" },
      { Header: t("warehousesPage.columns.employee.createdAt"), accessor: "created_at" },
    ],
    [handleOpenEmployeeDisable, handleOpenEmployeeEdit, t]
  );

  const stockDetailColumns = useMemo(
    () => [
      { Header: t("warehousesPage.columns.stockDetail.sku"), accessor: "sku" },
      { Header: t("warehousesPage.columns.stockDetail.qty"), accessor: "qty" },
      { Header: t("warehousesPage.columns.stockDetail.label"), accessor: "label" },
      { Header: t("warehousesPage.columns.stockDetail.price"), accessor: "price" },
      { Header: t("warehousesPage.columns.stockDetail.displayName"), accessor: "display_name" },
      { Header: t("warehousesPage.columns.stockDetail.productId"), accessor: "product_id" },
      {
        Header: t("warehousesPage.columns.stockDetail.actions"),
        accessor: "actions",
        Cell: (props) => (
          <MoveoutActionCell
            {...props}
            canMoveOut={canMoveOut}
            onOpen={handleOpenMoveout}
            label={t("warehousesPage.actions.moveout")}
            disabledLabel={t("warehousesPage.actions.disabled")}
          />
        ),
      },
    ],
    [canMoveOut, handleOpenMoveout, t]
  );

  const flattenWarehouseStocksForDownload = useCallback(
    (rows, warehouseColumns) =>
      rows.flatMap((row) => {
        const stocks = Array.isArray(row?.stocks) ? row.stocks : [];
        const baseRow = warehouseColumns.reduce((acc, column) => {
          acc[column.accessor] = row?.[column.accessor] ?? "";
          return acc;
        }, {});

        if (!stocks.length) {
          return [
            {
              ...baseRow,
              product_id: "",
              sku: "",
              qty: "",
              stock_label: "",
              price: "",
              display_name: "",
            },
          ];
        }

        return stocks.map((stock) => ({
          ...baseRow,
          product_id: stock?.product_id ?? "",
          sku: stock?.sku ?? "",
          qty: stock?.qty ?? "",
          stock_label: stock?.label ?? "",
          price: stock?.price ?? "",
          display_name: stock?.display_name ?? "",
        }));
      }),
    []
  );

  const downloadableTable = useMemo(() => {
    if (activeTab === "main") {
      const warehouseColumns = [
        { header: t("warehousesPage.columns.mainWarehouse.label"), accessor: "label" },
        {
          header: t("warehousesPage.columns.mainWarehouse.description"),
          accessor: "description",
        },
        { header: t("warehousesPage.columns.mainWarehouse.status"), accessor: "statut" },
        {
          header: t("warehousesPage.columns.mainWarehouse.warehouseId"),
          accessor: "warehouse_id",
        },
        {
          header: t("warehousesPage.columns.mainWarehouse.machineTpId"),
          accessor: "machine_tp_id",
        },
        { header: t("warehousesPage.columns.mainWarehouse.role"), accessor: "role" },
      ];

      return {
        filename: "warehouse-main",
        sheetName: "Main Warehouse",
        title: t("warehousesPage.sections.main"),
        columns: [
          ...warehouseColumns,
          { header: t("warehousesPage.columns.stockDetail.sku"), accessor: "sku" },
          { header: t("warehousesPage.columns.stockDetail.qty"), accessor: "qty" },
          {
            header: t("warehousesPage.columns.stockDetail.label"),
            accessor: "stock_label",
          },
          {
            header: t("warehousesPage.columns.stockDetail.price"),
            accessor: "price",
          },
          {
            header: t("warehousesPage.columns.stockDetail.displayName"),
            accessor: "display_name",
          },
          { header: t("warehousesPage.columns.stockDetail.productId"), accessor: "product_id" },
        ],
        rows: flattenWarehouseStocksForDownload(mainWarehouseRows, warehouseColumns),
      };
    }

    if (activeTab === "sub") {
      const warehouseColumns = [
        { header: t("warehousesPage.columns.childWarehouse.ref"), accessor: "ref" },
        { header: t("warehousesPage.columns.childWarehouse.label"), accessor: "label" },
        {
          header: t("warehousesPage.columns.childWarehouse.description"),
          accessor: "description",
        },
        { header: t("warehousesPage.columns.childWarehouse.status"), accessor: "statut" },
        {
          header: t("warehousesPage.columns.childWarehouse.warehouseId"),
          accessor: "warehouse_id",
        },
      ];

      return {
        filename: "warehouse-sub",
        sheetName: "Sub Warehouse",
        title: t("warehousesPage.sections.sub"),
        columns: [
          ...warehouseColumns,
          { header: t("warehousesPage.columns.stockDetail.sku"), accessor: "sku" },
          { header: t("warehousesPage.columns.stockDetail.qty"), accessor: "qty" },
          {
            header: t("warehousesPage.columns.stockDetail.label"),
            accessor: "stock_label",
          },
          {
            header: t("warehousesPage.columns.stockDetail.price"),
            accessor: "price",
          },
          {
            header: t("warehousesPage.columns.stockDetail.displayName"),
            accessor: "display_name",
          },
          { header: t("warehousesPage.columns.stockDetail.productId"), accessor: "product_id" },
        ],
        rows: flattenWarehouseStocksForDownload(childWarehouseRows, warehouseColumns),
      };
    }

    return null;
  }, [activeTab, childWarehouseRows, flattenWarehouseStocksForDownload, mainWarehouseRows, t]);

  // =================================================================
  // SECTION 5: Form handlers
  // =================================================================
  const resetChildOutForm = useCallback(() => {
    setChildOutForm({
      fromWarehouseId: "",
      sku: "",
      qty: "",
      batchNo: "",
      employeeCode: "",
      note: "",
    });
  }, []);

  const resetChildInForm = useCallback(() => {
    setChildInForm({
      toWarehouseId: "",
      sku: "",
      qty: "",
      batchNo: "",
      employeeCode: "",
      note: "",
      ref: "",
      venueId: "",
    });
  }, []);

  const resetMainInForm = useCallback(() => {
    setMainInForm({
      sku: "",
      qty: "",
      batchNo: "",
      employeeCode: "",
      note: "",
    });
  }, []);

  const handleCreateChildOut = useCallback(async () => {
    if (!email || !childOutForm.fromWarehouseId || !childOutForm.sku) {
      alert(t("warehousesPage.messages.requiredFields"));
      return;
    }
    setMissionSubmitting(true);
    try {
      await fetchJson(buildApiUrl(apiBase, "/api/v1/stock/createmissionv3"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          from_warehouse_id: Number(childOutForm.fromWarehouseId),
          to_warehouse_id: Number(warehouseData?.warehouse_id),
          sku: childOutForm.sku,
          qty_change: Number(childOutForm.qty),
          employee: childOutForm.employeeCode,
          batch_no: childOutForm.batchNo,
          note: childOutForm.note,
          email,
        }),
      });
      setMissionSuccess({ open: true, message: t("warehousesPage.messages.success") });
      setChildMissionOpen(false);
      resetChildOutForm();
      refreshAll();
    } catch (err) {
      console.error(err);
      setMissionError({ open: true, message: t("warehousesPage.messages.createFailed") });
    } finally {
      setMissionSubmitting(false);
    }
  }, [apiBase, childOutForm, email, refreshAll, resetChildOutForm, t, warehouseData]);

  const handleCreateChildIn = useCallback(async () => {
    if (!email || !childInForm.toWarehouseId || !childInForm.sku) {
      alert(t("warehousesPage.messages.requiredFields"));
      return;
    }
    setMissionSubmitting(true);
    try {
      const response = await fetchJson(buildApiUrl(apiBase, "/api/v1/stock/createmissionv3"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          from_warehouse_id: Number(warehouseData?.warehouse_id),
          to_warehouse_id: Number(childInForm.toWarehouseId),
          sku: childInForm.sku,
          qty_change: Number(childInForm.qty),
          employee: childInForm.employeeCode,
          batch_no: childInForm.batchNo,
          note: childInForm.note,
          email,
        }),
      });

      if (response?.data && childInForm.ref && childInForm.venueId) {
        await fetchJson(buildApiUrl(apiBase, "/api/v1/stockmovement/create"), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ref: childInForm.ref,
            stock_mission_id: response.data,
            email,
            venue_id: childInForm.venueId,
            ...(Number.isFinite(selectedChildInStock?.price)
              ? { price: Number(selectedChildInStock.price) }
              : {}),
          }),
        });
      }

      setMissionSuccess({ open: true, message: t("warehousesPage.messages.success") });
      setChildMissionOpen(false);
      resetChildInForm();
      refreshAll();
    } catch (err) {
      console.error(err);
      setMissionError({ open: true, message: t("warehousesPage.messages.createFailed") });
    } finally {
      setMissionSubmitting(false);
    }
  }, [
    apiBase,
    childInForm,
    email,
    refreshAll,
    resetChildInForm,
    selectedChildInStock,
    t,
    warehouseData,
  ]);

  const handleCreateMainIn = useCallback(async () => {
    if (!email || !mainInForm.sku) {
      alert(t("warehousesPage.messages.requiredFields"));
      return;
    }
    setMissionSubmitting(true);
    try {
      await fetchJson(buildApiUrl(apiBase, "/api/v1/stock/mainwarehouse"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          main_warehouse_id: MAIN_WAREHOUSE_ID,
          sku: mainInForm.sku,
          qty_change: Number(mainInForm.qty),
          employee: mainInForm.employeeCode,
          batch_no: mainInForm.batchNo,
          email,
          note: mainInForm.note,
        }),
      });
      setMissionSuccess({ open: true, message: t("warehousesPage.messages.success") });
      setMainMissionOpen(false);
      resetMainInForm();
      refreshAll();
    } catch (err) {
      console.error(err);
      setMissionError({ open: true, message: t("warehousesPage.messages.createFailed") });
    } finally {
      setMissionSubmitting(false);
    }
  }, [apiBase, email, mainInForm, refreshAll, resetMainInForm, t]);

  const closeMissionSuccess = useCallback(
    () => setMissionSuccess({ open: false, message: "" }),
    []
  );
  const closeMissionError = useCallback(() => setMissionError({ open: false, message: "" }), []);
  const handleOpenDownloadDialog = useCallback(() => {
    if (!downloadableTable) return;
    setDownloadFormat("csv");
    setDownloadDialogOpen(true);
  }, [downloadableTable]);
  const handleCloseDownloadDialog = useCallback(() => setDownloadDialogOpen(false), []);

  const handleDownloadTable = useCallback(() => {
    if (!downloadableTable) return;

    try {
      const headers = downloadableTable.columns.map((column) => column.header);
      const bodyRows = downloadableTable.rows.map((row) =>
        downloadableTable.columns.map((column) => {
          const rawValue = row?.[column.accessor];
          const value = column.format ? column.format(rawValue, row) : rawValue;

          if (value === null || value === undefined) return "";
          if (Array.isArray(value)) return value.join(", ");
          if (typeof value === "object") return JSON.stringify(value);
          return value;
        })
      );

      const worksheet = XLSX.utils.aoa_to_sheet([headers, ...bodyRows]);

      if (downloadFormat === "xlsx") {
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, downloadableTable.sheetName);
        XLSX.writeFile(workbook, `${downloadableTable.filename}.xlsx`);
      } else {
        const csv = `\uFEFF${XLSX.utils.sheet_to_csv(worksheet)}`;
        downloadBlob(
          new Blob([csv], { type: "text/csv;charset=utf-8;" }),
          `${downloadableTable.filename}.csv`
        );
      }

      setDownloadDialogOpen(false);
    } catch (error) {
      console.error(error);
      alert("下載失敗");
    }
  }, [downloadFormat, downloadableTable]);

  const renderMissionSuccessSB = (
    <MDSnackbar
      color="success"
      icon="check"
      title={t("warehousesPage.messages.success")}
      content={missionSuccess.message}
      dateTime="剛剛"
      open={missionSuccess.open}
      onClose={closeMissionSuccess}
      close={closeMissionSuccess}
      bgWhite
    />
  );
  const renderMissionErrorSB = (
    <MDSnackbar
      color="error"
      icon="warning"
      title={t("warehousesPage.messages.createFailed")}
      content={missionError.message}
      dateTime="剛剛"
      open={missionError.open}
      onClose={closeMissionError}
      close={closeMissionError}
      bgWhite
    />
  );

  const handleCreateEmployee = useCallback(async () => {
    if (!employeeCreateForm.code || !employeeCreateForm.firstName || !employeeCreateForm.lastName) {
      alert(t("warehousesPage.messages.requiredFields"));
      return;
    }
    try {
      await fetchJson(buildApiUrl(apiBase, "/api/v1/employee/create"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          code: employeeCreateForm.code,
          first_name: employeeCreateForm.firstName,
          last_name: employeeCreateForm.lastName,
          email,
          created_by_email: email,
        }),
      });
      alert(t("warehousesPage.messages.success"));
      setEmployeeCreateOpen(false);
      setEmployeeCreateForm({ code: "", firstName: "", lastName: "" });
      fetchEmployees();
    } catch (err) {
      console.error(err);
      alert(t("warehousesPage.messages.createFailed"));
    }
  }, [apiBase, email, employeeCreateForm, fetchEmployees, t]);

  const handleUpdateEmployee = useCallback(async () => {
    if (!employeeEditForm.code) {
      alert(t("warehousesPage.messages.missingEmployeeCode"));
      return;
    }
    try {
      if (employeeEditForm.originStatus !== employeeEditForm.status) {
        const statusPath =
          employeeEditForm.status === "active"
            ? `/api/v1/employee/restore/${encodeURIComponent(employeeEditForm.code)}`
            : `/api/v1/employee/delete/${encodeURIComponent(employeeEditForm.code)}`;
        const statusResponse = await fetch(buildApiUrl(apiBase, statusPath), {
          method: employeeEditForm.status === "active" ? "PUT" : "DELETE",
          headers: { "Mdm-Auth": mdmAuthKey },
        });

        if (!statusResponse.ok) {
          throw new Error(`Request failed (${statusResponse.status})`);
        }
      }

      await fetchJson(buildApiUrl(apiBase, "/api/v1/employee/update"), {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          code: employeeEditForm.code,
          first_name: employeeEditForm.firstName,
          last_name: employeeEditForm.lastName,
          email,
        }),
      });
      alert(t("warehousesPage.messages.success"));
      setEmployeeEditOpen(false);
      setEditingEmployee(null);
      fetchEmployees();
    } catch (err) {
      console.error(err);
      alert(t("warehousesPage.messages.updateFailed"));
    }
  }, [apiBase, email, employeeEditForm, fetchEmployees, mdmAuthKey, t]);

  const handleDisableEmployee = useCallback(async () => {
    if (!disableEmployeeTarget?.code) return;
    setDisableEmployeeSubmitting(true);
    try {
      const response = await fetch(
        buildApiUrl(
          apiBase,
          `/api/v1/employee/delete/${encodeURIComponent(disableEmployeeTarget.code)}`
        ),
        {
          method: "DELETE",
          headers: { "Mdm-Auth": mdmAuthKey },
        }
      );

      if (!response.ok) {
        throw new Error(`Request failed (${response.status})`);
      }

      alert("已停用");
      setDisableEmployeeTarget(null);
      if (editingEmployee?.code === disableEmployeeTarget.code) {
        setEmployeeEditOpen(false);
        setEditingEmployee(null);
      }
      fetchEmployees();
    } catch (err) {
      console.error(err);
      alert("停用失敗");
    } finally {
      setDisableEmployeeSubmitting(false);
    }
  }, [apiBase, disableEmployeeTarget, editingEmployee, fetchEmployees, mdmAuthKey]);

  // =================================================================
  // SECTION 6: Rendering
  // =================================================================
  const renderBkContent = () => {
    if (loadingBk) {
      return (
        <MDBox display="flex" alignItems="center" justifyContent="center" py={6}>
          <CircularProgress />
        </MDBox>
      );
    }

    if (bkError) {
      return (
        <MDTypography p={2} color="error">
          {bkError}
        </MDTypography>
      );
    }

    return (
      <MDBox px={3} pb={3}>
        <MDBox display="flex" flexWrap="wrap" gap={2} mb={3}>
          <ButtonGroup variant="outlined" sx={{ width: "100%", display: "flex" }}>
            <Button
              onClick={() => setActiveTab("main")}
              variant={activeTab === "main" ? "contained" : "outlined"}
              sx={{
                ...(activeTab === "main" ? primaryButtonSx : outlineButtonSx),
                flex: 1,
              }}
            >
              {t("warehousesPage.tabs.main")}
            </Button>
            <Button
              onClick={() => setActiveTab("sub")}
              variant={activeTab === "sub" ? "contained" : "outlined"}
              sx={{
                ...(activeTab === "sub" ? primaryButtonSx : outlineButtonSx),
                flex: 1,
              }}
            >
              {t("warehousesPage.tabs.sub")}
            </Button>
            <Button
              onClick={() => setActiveTab("missions")}
              variant={activeTab === "missions" ? "contained" : "outlined"}
              sx={{
                ...(activeTab === "missions" ? primaryButtonSx : outlineButtonSx),
                flex: 1,
              }}
            >
              {t("warehousesPage.tabs.missions")}
            </Button>
            <Button
              onClick={() => setActiveTab("logs")}
              variant={activeTab === "logs" ? "contained" : "outlined"}
              sx={{
                ...(activeTab === "logs" ? primaryButtonSx : outlineButtonSx),
                flex: 1,
              }}
            >
              {t("warehousesPage.tabs.logs")}
            </Button>
            <Button
              onClick={() => setActiveTab("employees")}
              variant={activeTab === "employees" ? "contained" : "outlined"}
              sx={{
                ...(activeTab === "employees" ? primaryButtonSx : outlineButtonSx),
                flex: 1,
              }}
            >
              {t("warehousesPage.tabs.employees")}
            </Button>
          </ButtonGroup>
        </MDBox>

        {activeTab === "main" && (
          <MDBox>
            <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <MDTypography variant="h6">{t("warehousesPage.sections.main")}</MDTypography>
              <Button
                variant="outlined"
                sx={outlineButtonSx}
                onClick={() => setMainMissionOpen(true)}
              >
                {t("warehousesPage.actions.stockMission")}
              </Button>
            </MDBox>
            <DataTable
              table={{ columns: mainWarehouseColumns, rows: mainWarehouseRows }}
              tabValue={activeTab}
              entriesPerPage={{ defaultValue: 10, entries: [10, 25, 50] }}
              canSearch
              showTotalEntries={false}
              isSorted
              noEndBorder
            />
          </MDBox>
        )}

        {activeTab === "sub" && (
          <MDBox>
            <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <MDTypography variant="h6">{t("warehousesPage.sections.sub")}</MDTypography>
              <Button
                variant="outlined"
                sx={outlineButtonSx}
                onClick={() => setChildMissionOpen(true)}
              >
                {t("warehousesPage.actions.stockMission")}
              </Button>
            </MDBox>
            <DataTable
              table={{ columns: childWarehouseColumns, rows: childWarehouseRows }}
              tabValue={activeTab}
              entriesPerPage={{ defaultValue: 10, entries: [10, 25, 50] }}
              canSearch
              showTotalEntries={false}
              isSorted
              noEndBorder
            />
          </MDBox>
        )}

        {activeTab === "missions" && (
          <MDBox>
            <MDTypography variant="h6" mb={2}>
              {t("warehousesPage.sections.missions")}
            </MDTypography>
            <DataTable
              table={{ columns: missionColumns, rows: missionRows }}
              tabValue={activeTab}
              entriesPerPage={{ defaultValue: 10, entries: [10, 25, 50] }}
              canSearch
              showTotalEntries={false}
              isSorted
              noEndBorder
            />
          </MDBox>
        )}

        {activeTab === "logs" && (
          <MDBox>
            <MDTypography variant="h6" mb={2}>
              {t("warehousesPage.sections.logs")}
            </MDTypography>
            <DataTable
              table={{ columns: stockLogColumns, rows: stockLogRows }}
              tabValue={activeTab}
              entriesPerPage={{ defaultValue: 10, entries: [10, 25, 50] }}
              canSearch
              showTotalEntries={false}
              isSorted
              noEndBorder
            />
          </MDBox>
        )}

        {activeTab === "employees" && (
          <MDBox>
            <MDBox display="flex" flexWrap="wrap" gap={2} alignItems="center" mb={2}>
              <MDTypography variant="h6">{t("warehousesPage.sections.employees")}</MDTypography>
              {showMerchantFilter && (
                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <InputLabel>{t("warehousesPage.form.merchant")}</InputLabel>
                  <Select
                    value={merchantFilter}
                    label={t("warehousesPage.form.merchant")}
                    onChange={(event) => setMerchantFilter(event.target.value)}
                    sx={selectSx}
                  >
                    <MenuItem value="BK">BK</MenuItem>
                    <MenuItem value="CM">CM</MenuItem>
                  </Select>
                </FormControl>
              )}
              <Button
                variant="outlined"
                sx={outlineButtonSx}
                onClick={() => setEmployeeCreateOpen(true)}
              >
                {t("warehousesPage.actions.createEmployee")}
              </Button>
            </MDBox>
            <DataTable
              table={{ columns: employeeColumns, rows: employeeRows }}
              tabValue={activeTab}
              entriesPerPage={{ defaultValue: 10, entries: [10, 25, 50] }}
              canSearch
              showTotalEntries={false}
              isSorted
              noEndBorder
            />
          </MDBox>
        )}
      </MDBox>
    );
  };

  const renderContent = () => {
    if (isBkRole) {
      if (useAppsmith) {
        return (
          <iframe
            key={userData?.email}
            src={appsmithUrl}
            width="100%"
            height="650px"
            frameBorder="0"
            title="Appsmith Warehouses"
          />
        );
      }
      // React implementation retained for later.
      return renderBkContent();
    }

    return <MDTypography p={2}>{t("warehousesPage.messages.loading")}</MDTypography>;
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                sx={{
                  px: 3,
                  display: "flex",
                  alignItems: { xs: "flex-start", sm: "center" },
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: 2,
                }}
              >
                <MDTypography variant="h6" color="white">
                  {t("warehouses.default", { ns: "routes" }) || t("pageTitles.warehouseBk")}
                </MDTypography>
                <MDBox height={40} display="flex" alignItems="center">
                  {(activeTab === "main" || activeTab === "sub") && (
                    <Button
                      id="download"
                      variant="outlined"
                      onClick={handleOpenDownloadDialog}
                      sx={{
                        borderRadius: "12px",
                        borderColor: "#FFFFFF",
                        color: "#FFFFFF",
                        fontWeight: 600,
                        "&:hover": {
                          borderColor: "#FFFFFF",
                          backgroundColor: "rgba(255,255,255,0.15)",
                        },
                      }}
                    >
                      匯出表格
                    </Button>
                  )}
                </MDBox>
              </MDBox>

              <MDBox pt={3} px={0} width="100%">
                {renderContent()}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <Dialog open={stockModal.open} onClose={handleCloseStockModal} fullWidth maxWidth="md">
        <DialogTitle>
          {stockModal.title
            ? t("warehousesPage.dialogs.stockDetailWithName", { name: stockModal.title })
            : t("warehousesPage.dialogs.stockDetail")}
        </DialogTitle>
        <DialogContent dividers>
          <DataTable
            table={{ columns: stockDetailColumns, rows: stockModalRows }}
            entriesPerPage={{ defaultValue: 10, entries: [10, 25, 50] }}
            canSearch
            showTotalEntries={false}
            isSorted
            noEndBorder
          />
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" sx={outlineButtonSx} onClick={handleCloseStockModal}>
            {t("warehousesPage.actions.close")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={downloadDialogOpen} onClose={handleCloseDownloadDialog} fullWidth maxWidth="xs">
        <DialogTitle>匯出表格</DialogTitle>
        <DialogContent dividers>
          <FormControl component="fieldset" fullWidth>
            <RadioGroup
              value={downloadFormat}
              onChange={(event) => setDownloadFormat(event.target.value)}
            >
              <FormControlLabel value="csv" control={<Radio />} label="CSV" />
              <FormControlLabel value="xlsx" control={<Radio />} label="XLSX" />
            </RadioGroup>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            onClick={handleCloseDownloadDialog}
            sx={{
              borderRadius: "10px",
              borderColor: "#1A73E8",
              color: "#1A73E8",
              fontWeight: 600,
              "&:hover": {
                borderColor: "#1557B0",
                backgroundColor: "rgba(26, 115, 232, 0.08)",
              },
            }}
          >
            取消
          </Button>
          <Button
            variant="contained"
            onClick={handleDownloadTable}
            sx={{
              borderRadius: "10px",
              backgroundColor: "#1A73E8",
              color: "#FFFFFF",
              fontWeight: 600,
              "&:hover": {
                backgroundColor: "#1557B0",
              },
            }}
          >
            確認下載
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(calibrationTarget)}
        onClose={handleCloseCalibration}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>機台校正</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="倉庫名稱"
                fullWidth
                size="small"
                value={calibrationWarehouseName}
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                fullWidth
                size="small"
                options={calibrationStockOptions}
                value={selectedCalibrationStock}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                getOptionLabel={(option) => option?.label || ""}
                onChange={(event, selectedOption) =>
                  setCalibrationForm((prev) => ({
                    ...prev,
                    sku: selectedOption?.value || "",
                    targetQty:
                      selectedOption?.qty !== null &&
                      selectedOption?.qty !== undefined &&
                      !Number.isNaN(selectedOption.qty)
                        ? String(selectedOption.qty)
                        : "",
                  }))
                }
                renderInput={(params) => <TextField {...params} label="商品名稱" />}
                sx={autocompleteSx}
                disabled={calibrationSubmitting}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="目標庫存數量"
                fullWidth
                size="small"
                type="number"
                value={calibrationForm.targetQty}
                onChange={(event) =>
                  setCalibrationForm((prev) => ({ ...prev, targetQty: event.target.value }))
                }
                inputProps={{ min: 0, step: 1 }}
                disabled={calibrationSubmitting}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="調整原因"
                fullWidth
                size="small"
                multiline
                minRows={3}
                value={calibrationForm.reason}
                onChange={(event) =>
                  setCalibrationForm((prev) => ({ ...prev, reason: event.target.value }))
                }
                disabled={calibrationSubmitting}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            sx={outlineButtonSx}
            onClick={handleCloseCalibration}
            disabled={calibrationSubmitting}
          >
            關閉
          </Button>
          <Button
            variant="contained"
            sx={primaryButtonSx}
            onClick={handleSubmitCalibration}
            disabled={calibrationSubmitting}
          >
            {calibrationSubmitting ? "送出中" : "確認"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={moveoutOpen} onClose={handleCloseMoveout} fullWidth maxWidth="sm">
        <DialogTitle>{t("warehousesPage.dialogs.moveout")}</DialogTitle>
        <DialogContent dividers>
          {moveoutError && (
            <MDTypography variant="body2" color="error" mb={2}>
              {moveoutError}
            </MDTypography>
          )}
          {moveoutSuccess && (
            <MDTypography variant="body2" color="success" mb={2}>
              {moveoutSuccess}
            </MDTypography>
          )}
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label={t("warehousesPage.form.sku")}
                value={moveoutTarget?.sku || moveoutTarget?.product_id || ""}
                fullWidth
                size="small"
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label={t("warehousesPage.form.thirdPartyId")}
                value={moveoutThirdPartyId}
                fullWidth
                size="small"
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label={t("warehousesPage.form.moveoutQty")}
                value={moveoutQty}
                onChange={(event) => setMoveoutQty(event.target.value)}
                fullWidth
                size="small"
                type="number"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label={t("warehousesPage.form.email")}
                value={email}
                fullWidth
                size="small"
                InputProps={{ readOnly: true }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" sx={outlineButtonSx} onClick={handleCloseMoveout}>
            {t("warehousesPage.actions.close")}
          </Button>
          <Button
            variant="outlined"
            sx={outlineButtonSx}
            onClick={handleConfirmMoveout}
            disabled={movingOut}
          >
            {movingOut
              ? t("warehousesPage.actions.movingOut")
              : t("warehousesPage.actions.moveoutConfirm")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={childMissionOpen}
        onClose={() => setChildMissionOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>{t("warehousesPage.dialogs.childMission")}</DialogTitle>
        <DialogContent
          dividers
          sx={{ display: "flex", flexDirection: "column", gap: 2, position: "relative" }}
        >
          <Backdrop
            open={missionSubmitting}
            sx={{
              position: "absolute",
              inset: 0,
              color: "#fff",
              zIndex: (theme) => theme.zIndex.modal + 1,
            }}
          >
            <CircularProgress color="inherit" />
          </Backdrop>
          <ButtonGroup variant="outlined">
            <Button
              variant={childMissionTab === "in" ? "contained" : "outlined"}
              onClick={() => setChildMissionTab("in")}
              sx={childMissionTab === "in" ? primaryButtonSx : outlineButtonSx}
            >
              {t("warehousesPage.actions.childIn")}
            </Button>
            <Button
              variant={childMissionTab === "out" ? "contained" : "outlined"}
              onClick={() => setChildMissionTab("out")}
              sx={childMissionTab === "out" ? primaryButtonSx : outlineButtonSx}
            >
              {t("warehousesPage.actions.childOut")}
            </Button>
          </ButtonGroup>

          {childMissionTab === "out" && (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label={t("warehousesPage.form.email")}
                  value={email}
                  fullWidth
                  size="small"
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  fullWidth
                  size="small"
                  options={childWarehouseOptions}
                  value={
                    childWarehouseOptions.find(
                      (option) => option.value === childOutForm.fromWarehouseId
                    ) || null
                  }
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                  getOptionLabel={(option) => option?.label || ""}
                  onChange={(event, selectedOption) =>
                    setChildOutForm((prev) => ({
                      ...prev,
                      fromWarehouseId: selectedOption?.value || "",
                    }))
                  }
                  renderInput={(params) => (
                    <TextField {...params} label={t("warehousesPage.form.fromWarehouse")} />
                  )}
                  sx={autocompleteSx}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  fullWidth
                  size="small"
                  options={childOutSkuOptions}
                  value={
                    childOutSkuOptions.find((option) => option.value === childOutForm.sku) || null
                  }
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                  getOptionLabel={(option) => option?.label || ""}
                  onChange={(event, selectedOption) =>
                    setChildOutForm((prev) => ({
                      ...prev,
                      sku: selectedOption?.value || "",
                    }))
                  }
                  renderInput={(params) => (
                    <TextField {...params} label={t("warehousesPage.form.sku")} />
                  )}
                  sx={autocompleteSx}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label={t("warehousesPage.form.qtyOut")}
                  value={childOutForm.qty}
                  onChange={(event) =>
                    setChildOutForm((prev) => ({ ...prev, qty: event.target.value }))
                  }
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label={t("warehousesPage.form.batchNo")}
                  value={childOutForm.batchNo}
                  onChange={(event) =>
                    setChildOutForm((prev) => ({ ...prev, batchNo: event.target.value }))
                  }
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>{t("warehousesPage.form.employee")}</InputLabel>
                  <Select
                    value={childOutForm.employeeCode}
                    label={t("warehousesPage.form.employee")}
                    onChange={(event) =>
                      setChildOutForm((prev) => ({ ...prev, employeeCode: event.target.value }))
                    }
                    sx={selectSx}
                  >
                    {employeeOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label={t("warehousesPage.form.note")}
                  value={childOutForm.note}
                  onChange={(event) =>
                    setChildOutForm((prev) => ({ ...prev, note: event.target.value }))
                  }
                  fullWidth
                  size="small"
                />
              </Grid>
            </Grid>
          )}

          {childMissionTab === "in" && (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label={t("warehousesPage.form.email")}
                  value={email}
                  fullWidth
                  size="small"
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  fullWidth
                  size="small"
                  options={childWarehouseOptions}
                  value={
                    childWarehouseOptions.find(
                      (option) => option.value === childInForm.toWarehouseId
                    ) || null
                  }
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                  getOptionLabel={(option) => option?.label || ""}
                  onChange={(event, selectedOption) =>
                    setChildInForm((prev) => ({
                      ...prev,
                      toWarehouseId: selectedOption?.value || "",
                    }))
                  }
                  renderInput={(params) => (
                    <TextField {...params} label={t("warehousesPage.form.toWarehouse")} />
                  )}
                  sx={autocompleteSx}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  fullWidth
                  size="small"
                  options={mainStockOptions}
                  value={
                    mainStockOptions.find((option) => option.value === childInForm.sku) || null
                  }
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                  getOptionLabel={(option) => option?.label || ""}
                  onChange={(event, selectedOption) =>
                    setChildInForm((prev) => ({
                      ...prev,
                      sku: selectedOption?.value || "",
                    }))
                  }
                  renderInput={(params) => (
                    <TextField {...params} label={t("warehousesPage.form.sku")} />
                  )}
                  sx={autocompleteSx}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label={t("warehousesPage.form.qtyIn")}
                  value={childInForm.qty}
                  onChange={(event) =>
                    setChildInForm((prev) => ({ ...prev, qty: event.target.value }))
                  }
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label={t("warehousesPage.form.batchNo")}
                  value={childInForm.batchNo}
                  onChange={(event) =>
                    setChildInForm((prev) => ({ ...prev, batchNo: event.target.value }))
                  }
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>{t("warehousesPage.form.employee")}</InputLabel>
                  <Select
                    value={childInForm.employeeCode}
                    label={t("warehousesPage.form.employee")}
                    onChange={(event) =>
                      setChildInForm((prev) => ({ ...prev, employeeCode: event.target.value }))
                    }
                    sx={selectSx}
                  >
                    {employeeOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label={t("warehousesPage.form.ref")}
                  value={childInForm.ref}
                  onChange={(event) =>
                    setChildInForm((prev) => ({ ...prev, ref: event.target.value }))
                  }
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>{t("warehousesPage.form.venue")}</InputLabel>
                  <Select
                    value={childInForm.venueId}
                    label={t("warehousesPage.form.venue")}
                    onChange={(event) =>
                      setChildInForm((prev) => ({ ...prev, venueId: event.target.value }))
                    }
                    sx={selectSx}
                  >
                    {venueOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label={t("warehousesPage.form.note")}
                  value={childInForm.note}
                  onChange={(event) =>
                    setChildInForm((prev) => ({ ...prev, note: event.target.value }))
                  }
                  fullWidth
                  size="small"
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            sx={outlineButtonSx}
            onClick={() => setChildMissionOpen(false)}
          >
            {t("warehousesPage.actions.close")}
          </Button>
          <Button
            variant="contained"
            onClick={childMissionTab === "in" ? handleCreateChildIn : handleCreateChildOut}
            sx={primaryButtonSx}
          >
            {t("warehousesPage.actions.confirm")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={mainMissionOpen}
        onClose={() => setMainMissionOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{t("warehousesPage.dialogs.mainMission")}</DialogTitle>
        <DialogContent dividers sx={{ position: "relative" }}>
          <Backdrop
            open={missionSubmitting}
            sx={{
              position: "absolute",
              inset: 0,
              color: "#fff",
              zIndex: (theme) => theme.zIndex.modal + 1,
            }}
          >
            <CircularProgress color="inherit" />
          </Backdrop>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label={t("warehousesPage.form.email")}
                value={email}
                fullWidth
                size="small"
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                fullWidth
                size="small"
                options={productSkuOptions}
                value={productSkuOptions.find((option) => option.value === mainInForm.sku) || null}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                getOptionLabel={(option) => option?.label || ""}
                onChange={(event, selectedOption) =>
                  setMainInForm((prev) => ({
                    ...prev,
                    sku: selectedOption?.value || "",
                  }))
                }
                renderInput={(params) => (
                  <TextField {...params} label={t("warehousesPage.form.sku")} />
                )}
                sx={autocompleteSx}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label={t("warehousesPage.form.qtyIn")}
                value={mainInForm.qty}
                onChange={(event) =>
                  setMainInForm((prev) => ({ ...prev, qty: event.target.value }))
                }
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label={t("warehousesPage.form.batchNo")}
                value={mainInForm.batchNo}
                onChange={(event) =>
                  setMainInForm((prev) => ({ ...prev, batchNo: event.target.value }))
                }
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>{t("warehousesPage.form.employee")}</InputLabel>
                <Select
                  value={mainInForm.employeeCode}
                  label={t("warehousesPage.form.employee")}
                  onChange={(event) =>
                    setMainInForm((prev) => ({ ...prev, employeeCode: event.target.value }))
                  }
                  sx={selectSx}
                >
                  {employeeOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label={t("warehousesPage.form.note")}
                value={mainInForm.note}
                onChange={(event) =>
                  setMainInForm((prev) => ({ ...prev, note: event.target.value }))
                }
                fullWidth
                size="small"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" sx={outlineButtonSx} onClick={() => setMainMissionOpen(false)}>
            {t("warehousesPage.actions.close")}
          </Button>
          <Button variant="contained" onClick={handleCreateMainIn} sx={primaryButtonSx}>
            {t("warehousesPage.actions.confirm")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={employeeCreateOpen}
        onClose={() => setEmployeeCreateOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{t("warehousesPage.dialogs.employeeCreate")}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label={t("warehousesPage.form.code")}
                value={employeeCreateForm.code}
                onChange={(event) =>
                  setEmployeeCreateForm((prev) => ({ ...prev, code: event.target.value }))
                }
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label={t("warehousesPage.form.firstName")}
                value={employeeCreateForm.firstName}
                onChange={(event) =>
                  setEmployeeCreateForm((prev) => ({ ...prev, firstName: event.target.value }))
                }
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label={t("warehousesPage.form.lastName")}
                value={employeeCreateForm.lastName}
                onChange={(event) =>
                  setEmployeeCreateForm((prev) => ({ ...prev, lastName: event.target.value }))
                }
                fullWidth
                size="small"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            sx={outlineButtonSx}
            onClick={() => setEmployeeCreateOpen(false)}
          >
            {t("warehousesPage.actions.close")}
          </Button>
          <Button variant="contained" onClick={handleCreateEmployee} sx={primaryButtonSx}>
            {t("warehousesPage.actions.confirm")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={employeeEditOpen}
        onClose={() => setEmployeeEditOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{t("warehousesPage.dialogs.employeeEdit")}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label={t("warehousesPage.form.code")}
                value={employeeEditForm.code}
                fullWidth
                size="small"
                disabled
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label={t("warehousesPage.form.firstName")}
                value={employeeEditForm.firstName}
                onChange={(event) =>
                  setEmployeeEditForm((prev) => ({ ...prev, firstName: event.target.value }))
                }
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label={t("warehousesPage.form.lastName")}
                value={employeeEditForm.lastName}
                onChange={(event) =>
                  setEmployeeEditForm((prev) => ({ ...prev, lastName: event.target.value }))
                }
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <MDBox display="flex" alignItems="center" gap={2} flexWrap="wrap">
                <MDTypography variant="button" fontWeight="regular">
                  {`目前狀態：${employeeEditForm.status === "active" ? "啟用" : "停用"}`}
                </MDTypography>
              </MDBox>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            sx={outlineButtonSx}
            onClick={() => setEmployeeEditOpen(false)}
          >
            {t("warehousesPage.actions.close")}
          </Button>
          <Button variant="contained" onClick={handleUpdateEmployee} sx={primaryButtonSx}>
            {t("warehousesPage.actions.confirm")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(disableEmployeeTarget)}
        onClose={handleCloseEmployeeDisable}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>確認停用</DialogTitle>
        <DialogContent dividers>
          <MDTypography variant="button" fontWeight="regular">
            是否確定停用員工 {disableEmployeeTarget?.code || "-"}？
          </MDTypography>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            sx={outlineButtonSx}
            onClick={handleCloseEmployeeDisable}
            disabled={disableEmployeeSubmitting}
          >
            取消
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDisableEmployee}
            disabled={disableEmployeeSubmitting}
          >
            {disableEmployeeSubmitting ? "停用中..." : "確認停用"}
          </Button>
        </DialogActions>
      </Dialog>
      {renderMissionSuccessSB}
      {renderMissionErrorSB}
    </DashboardLayout>
  );
}

export default WarehousesBK;
